import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';

export interface SuggestedPriceZone {
  id: string;
  name: string;
  regions: string[];
  provinces?: string[];
  districts?: string[];
}

export interface CreateSuggestedPriceZoneDto {
  Name: string;
  Regions: string[];
  Provinces?: string[];
  Districts?: string[];
}

type PossiblePagedResponse<T> =
  | T[]
  | {
      items?: T[];
      results?: T[];
      data?: T[];
    }
  | unknown;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const get = (obj: unknown, key: string): unknown => (isRecord(obj) ? obj[key] : undefined);

const normalizeSuggestedZone = (z: unknown) => ({
  id: String(get(z, 'id') ?? get(z, 'Id') ?? ''),
  name: String(get(z, 'name') ?? get(z, 'Name') ?? ''),
  regions: (get(z, 'regions') ?? get(z, 'Regions') ?? []) as string[],
  provinces: (get(z, 'provinces') ?? get(z, 'Provinces') ?? []) as string[],
  districts: (get(z, 'districts') ?? get(z, 'Districts') ?? []) as string[],
});

export const suggestedPriceZoneApi = createApi({
  reducerPath: 'suggestedPriceZoneApi',
  baseQuery,
  tagTypes: ['SuggestedPriceZone'],
  endpoints: (builder) => ({
    getSuggestedPriceZone: builder.query<SuggestedPriceZone, string>({
      query: (id) => ({ url: `/api/suggestedpricezones/${id}`, method: 'GET' }),
      transformResponse: (response: unknown) => normalizeSuggestedZone(response),
      providesTags: (_res, _err, id) => [{ type: 'SuggestedPriceZone', id }],
    }),

    updateSuggestedPriceZone: builder.mutation<
      void,
      { id: string } & Partial<CreateSuggestedPriceZoneDto>
    >({
      query: ({ id, ...body }) => ({
        url: `/api/suggestedpricezones/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'SuggestedPriceZone', id: arg.id },
        { type: 'SuggestedPriceZone', id: 'LIST' },
      ],
    }),

    suggestedPriceZoneList: builder.query<SuggestedPriceZone[], void>({
      query: () => ({
        url: `/api/suggestedpricezones?pageNumber=1&pageSize=1000`,
        method: 'GET',
      }),
      transformResponse: (response: PossiblePagedResponse<SuggestedPriceZone>) => {
        if (!response) return [];
        if (Array.isArray(response)) return response.map(normalizeSuggestedZone);

        const pickArray = (v: unknown) => {
          if (Array.isArray(v)) return v;
          if (isRecord(v) && Array.isArray(v.$values)) return v.$values;
          return null;
        };

        const direct =
          pickArray(get(response, 'items')) ??
          pickArray(get(response, 'Items')) ??
          pickArray(get(response, 'results')) ??
          pickArray(get(response, 'Results')) ??
          pickArray(get(response, 'data')) ??
          pickArray(get(response, 'Data'));

        if (direct) return direct.map(normalizeSuggestedZone);

        const paged = get(response, 'paged');
        const pagination = get(response, 'pagination');

        const nested =
          pickArray(get(paged, 'items')) ??
          pickArray(get(paged, 'Items')) ??
          pickArray(get(pagination, 'items')) ??
          pickArray(get(pagination, 'Items'));

        return (nested ?? []).map(normalizeSuggestedZone);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((z) => ({ type: 'SuggestedPriceZone' as const, id: z.id })),
              { type: 'SuggestedPriceZone' as const, id: 'LIST' },
            ]
          : [{ type: 'SuggestedPriceZone' as const, id: 'LIST' }],
    }),

    createSuggestedPriceZone: builder.mutation<void, CreateSuggestedPriceZoneDto>({
      query: (body) => ({
        url: `/api/suggestedpricezones`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'SuggestedPriceZone', id: 'LIST' }],
    }),

    deleteSuggestedPriceZone: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/suggestedpricezones/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'SuggestedPriceZone', id: 'LIST' }],
    }),
  }),
});

export const {
  useSuggestedPriceZoneListQuery,
  useLazySuggestedPriceZoneListQuery,
  useCreateSuggestedPriceZoneMutation,
  useDeleteSuggestedPriceZoneMutation,
  useGetSuggestedPriceZoneQuery,
  useUpdateSuggestedPriceZoneMutation,
} = suggestedPriceZoneApi;
